import { duration } from '@play-co/components';
import StateObserver from 'src/StateObserver';
import { useCache } from 'src/lib/utils';
import type {
  CardsCandidate,
  CardsFriend,
  CardsFriendMap,
} from 'src/replicant/cards/cards.asyncGetters';
import type { CardId } from 'src/replicant/cards/cards.ruleset';

/**
 * Retrieve users friends and their card collections
 */
export async function cardsGetFriends(): Promise<CardsFriendMap> {
  const result = await useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.cardsGetFriends({});
    },
    `cardsGetFriends`,
    duration({ minutes: 10 }),
  ).get();
  return result;
}

/**
 * Retrieve a specific friend and their card collection
 */
export async function cardsGetFriend(
  id: string,
): Promise<CardsFriend | undefined> {
  const players = await cardsGetFriends();
  return players[id];
}

/**
 * Retrieve players who can receive a card trade proposal.
 *
 * Results are cached per card so reopening the trade picker does not repeat
 * the same Replicant search while the data is still fresh.
 */
export function cardsGetTradeCandidates(cardId: CardId) {
  return useCache<CardsCandidate[]>(
    async () => {
      return StateObserver.replicant.asyncGetters.cardsGetTradeCandidates({
        cardId,
      });
    },
    `cardsGetTradeCandidates_${cardId}`,
    duration({ minutes: 10 }),
  );
}

/**
 * Remove a player from the cached trade candidates for a specific card.
 *
 * Used after successfully proposing a trade, so the player does not appear
 * again if the picker is reopened before the cache expires.
 */
export function cardsRemoveTradeCandidateFromCache(
  cardId: CardId,
  playerId: string,
) {
  cardsGetTradeCandidates(cardId).setCached((cached) => {
    if (!cached) return undefined;
    cached.data = cached.data.filter((candidate) => candidate.id !== playerId);
    return cached;
  });
}

/**
 * Retrieve players who can be asked for a missing card.
 *
 * Results are cached per card so reopening the request picker does not repeat
 * the same Replicant search while the data is still fresh.
 */
export function cardsGetRequestCandidates(cardId: CardId) {
  return useCache<CardsCandidate[]>(
    async () => {
      return StateObserver.replicant.asyncGetters.cardsGetRequestCandidates({
        cardId,
      });
    },
    `cardsGetRequestCandidates_${cardId}`,
    duration({ minutes: 10 }),
  );
}

/**
 * Remove a player from the cached request candidates for a specific card.
 *
 * Used after selecting a candidate from the request picker, so the player does
 * not appear again if the picker is reopened before the cache expires.
 */
export function cardsRemoveRequestCandidateFromCache(
  cardId: CardId,
  playerId: string,
) {
  cardsGetRequestCandidates(cardId).setCached((cached) => {
    if (!cached) return undefined;
    cached.data = cached.data.filter((candidate) => candidate.id !== playerId);
    return cached;
  });
}
